import { plainToClass } from "class-transformer";
import { AppUserExtended, JsonItem, PickListItem, IControllerFilter, PickListItemSorted, AnimalModel, RevamppCore, RevamppProject, PickListItemString } from "../../../list-management/scripts/list-management.objects";
import { FilterTab, Filter, DropdownGroup } from "../../../list-management/scripts/list-management.filters";
import { AnimalModelsEditControllerConfig, AnimalModelsTableConfig, AnimalModelOptGroupFilterItem } from "./edit-animal-models.objects";
import { InitializeFilterDropdown } from "../../../../shared/scripts/dropdown-functions";
import { getDisplayDate } from '../../../../shared/scripts/date-functions';
import { TableColumnGroup, TableColumnVisibilityControllerV2 } from "../../../../shared/scripts/table-column-visibility";
import { EditAnimalModelController, AddAnimalModelController } from "./edit-animal-models.controllers";
import _ from "lodash";

export class AnimalModelsTable implements IControllerFilter {
    // Config Variables
    tableContainerId: string;
    getAnimalModelsRoute: string;
    loadingContainerId: string;
    loadedContentId: string;
    isAdmin: boolean;
    isEditable: boolean;
    tableVarName: string;
    additionalInfoModalId: string;

    // Table Variables
    id: string = "revampp-animal-models";
    referenceId: string = "am";
    animalModels: AnimalModel[] = [];
    pickLists: JsonItem[] = [];
    sortedPickLists: JsonItem[] = [];
    allPickLists: JsonItem[] = [];
    animalModelTable: any;
    tableColumnGroups: TableColumnGroup[];
    tableController: TableColumnVisibilityControllerV2;
    currentTargetItem: any;
    visibleAnimalModels: AnimalModel[] = [];

    // Edit Controllers
    editController: EditAnimalModelController;

    // Add Controllers
    addController: AddAnimalModelController;

    // TBD (may move these to config if needed)
    filterTabs: FilterTab[];
    columns: any[];

    constructor(config: AnimalModelsTableConfig) {
        this.tableContainerId = config.tableContainerId;
        this.getAnimalModelsRoute = config.getAnimalModelsRoute;
        this.loadingContainerId = config.loadingContainerId;
        this.loadedContentId = config.loadedContentId;
        this.isEditable = config.isEditable;
        this.isAdmin = config.isAdmin;
        this.tableVarName = config.tableVarName;
        this.additionalInfoModalId = config.additionalInfoModalId;

        this.filterTabs = [
            new FilterTab("virus", "Virus", "Virus",
                [
                    new Filter("am-purpose-filter", "Model Purpose", "multi-select-pl-multi", "ModelPurposeIds", "pl-am-model-purpose"),
                    new Filter("am-virus-family-filter", "Virus Family", "search-multi-select-pl-single", "VirusFamilyId", "pl-virus-family"),
                    new Filter("am-challenge-virus-filter", "Challenge Virus(es)", "search-multi-select-pl-multi", "ChallengeVirusIds", "pl-virus"),
                    new Filter("am-virus-route-filter", "Virus Route", "multi-select-pl-multi", "VirusRouteIds", "pl-am-route")
                ]
            ),
            new FilterTab("animal", "Animal", "Animal",
                [
                    new Filter("am-species-filter", "Species", "multi-select-pl-single", "SpeciesId", "pl-am-species"),
                    new Filter("am-strain-filter", "Strain", "multi-select-pl-multi", "StrainIds", "pl-am-strain"),
                    new Filter("am-endpoint-filter", "Endpoint(s)", "multi-select-pl-multi", "EndpointIds", "pl-am-endpoint"),
                    new Filter("am-sex-filter", "Sex", "multi-select-pl-multi", "SexIds", "pl-am-sex"),
                    new Filter("am-absl-filter", "ABSL", "multi-select-pl-single", "AbslId", "pl-absl")
                ]
            ),
            new FilterTab("developer", "Developer", "Developer",
                [
                    new Filter("am-center-filter", "Center", "multi-select-pl-single", "CenterId", "pl-center"),
                    new Filter("am-core-project-filter", "Core/Project", "am-core-project-filter", "CenterId", "pl-center", ["CoreIds", "ProjectIds"], ["pl-core", "pl-project"]),
                    //new Filter("am-core-filter", "Core", "multi-select-pl-multi", "CoreIds", "pl-core-center"),
                    //new Filter("am-project-filter", "Project", "multi-select-pl-multi", "ProjectIds", "pl-project-center"),
                    new Filter("am-model-status-filter", "Model Status", "multi-select-pl-single", "ModelStatusId", "pl-am-model-status"),
                    new Filter("am-model-developer-filter", "Model Developer", "search-multi-select-pl-single", "ChampionId", "pl-user")
                ]
            ),
        ]

        this.columns = [
            { data: "ModelName", title: "Model Name", className: "text-left font-size12 table-column-md" },
            { data: "ModelPurposeIds", title: "Model Purpose", className: "virus text-left font-size12 table-column-md", render: (data, type) => { return this.renderPickListItem(data, "pl-am-model-purpose", true) } },
            { data: "VirusFamilyId", title: "Virus Family", className: "virus text-left font-size12 nowrap", render: (data, type) => { return this.renderPickListItem(data, "pl-virus-family") } },
            { data: "ChallengeVirusIds", title: "Challenge Virus(es)", className: "virus text-left font-size12 table-column-md", render: (data, type) => { return this.renderPickListItem(data, "pl-virus", true) } },
            { data: "VirusRouteIds", title: "Virus Route", className: "virus text-left font-size12 nowrap", render: (data, type) => { return this.renderPickListItem(data, "pl-am-route", true) } },

            { data: "SpeciesId", title: "Species", className: "animal text-left font-size12 nowrap", render: (data, type) => { return this.renderPickListItem(data, "pl-am-species") } },
            { data: "StrainIds", title: "Strain", className: "animal text-left font-size12 nowrap", render: (data, type) => { return this.renderPickListItem(data, "pl-am-strain", true) } },
            { data: "EndpointIds", title: "Endpoint(s)", className: "animal text-left font-size12 table-column-md", render: (data, type) => { return this.renderPickListItem(data, "pl-am-endpoint", true) } },
            { data: "SexIds", title: "Sex", className: "animal text-left font-size12 nowrap", render: (data, type) => { return this.renderPickListItem(data, "pl-am-sex", true) } },
            { data: "AbslId", title: "ABSL", className: "animal text-left font-size12 nowrap", render: (data, type) => { return this.renderPickListItem(data, "pl-absl") } },

            { data: "CenterId", title: "Center", className: "developer text-left font-size12 nowrap", render: (data, type) => { return this.renderPickListItem(data, "pl-center") } },
            { data: null, title: "Core/Project", className: "developer text-left font-size12 table-column-lg lh-normal", render: (data) => { return this.renderCoreProject(data) } },
            //{ data: "CoreIds", title: "Cores", className: "developer text-left font-size12 table-column-md", render: (data, type) => { return this.renderPickListItem(data, "pl-core", true, false, "<br>") } },
            //{ data: "ProjectIds", title: "Projects", className: "developer text-left font-size12 table-column-md", render: (data, type) => { return this.renderPickListItem(data, "pl-project", true, false, "<br>") } },
            { data: "ModelStatusId", title: "Model Status", className: "developer text-left font-size12 nowrap", render: (data, type) => { return this.renderPickListItem(data, "pl-am-model-status") } },
            { data: "ChampionId", title: "Model Developer", className: "developer text-left font-size12 nowrap", render: (data, type) => { return this.renderPickListItem(data, "pl-user") } },
        ]

        this.tableColumnGroups = [
            new TableColumnGroup("Virus", true, [], "virus"),
            new TableColumnGroup("Animal", false, [], "animal"),
            new TableColumnGroup("Developer", false, [], "developer")
        ]

        if (this.isEditable) {
            let editConfig: AnimalModelsEditControllerConfig = {
                isAdmin: config.isAdmin,
                modalIdTarget: config.editModalId,
                submitRoute: config.postRoute,
                tableVarName: config.tableVarName
            }

            this.editController = new EditAnimalModelController(editConfig, this);

            let addConfig: AnimalModelsEditControllerConfig = {
                isAdmin: config.isAdmin,
                modalIdTarget: config.addModalId,
                submitRoute: config.postRoute,
                tableVarName: config.tableVarName
            }
            this.addController = new AddAnimalModelController(addConfig, this);
        }

        this.initialize();
    }

    async initialize() {
        this.loadingDisplay(true);

        let result: any[] = await this.getAnimalModels();
        if (result["items"]) {
            this.animalModels = plainToClass(AnimalModel, <AnimalModel[]>result["items"]);
        }
        if (result["pickLists"]) {
            this.pickLists = plainToClass(JsonItem, <JsonItem[]>result["pickLists"]);
        }
        if (result["sortedPickLists"]) {
            this.sortedPickLists = plainToClass(JsonItem, <JsonItem[]>result["sortedPickLists"]);
        }

        this.allPickLists = [...this.pickLists, ...this.sortedPickLists];

        this.createHtmlTemplate();
        this.initializeFilterAndTable();

        this.loadingDisplay(false);
        this.resizeTable();
    }

    async getAnimalModels() {
        let result: any;
        try {
            result = await $.ajax(this.getAnimalModelsRoute, {
                method: "GET",
                headers: { "RequestVerificationToken": <string>$('input[name="__RequestVerificationToken"]').val() },
                cache: false
            });
            return result;
        }
        catch (error) {
            alert("An error has occurred, please refresh the page and try again.");
            console.log(error);
        }
    }

    loadingDisplay(isLoading: boolean): void {
        if (isLoading) {
            $(`#${this.loadedContentId}`).addClass("d-none");
            $(`#${this.loadingContainerId}`).removeClass("d-none");
        }
        else {
            $(`#${this.loadedContentId}`).removeClass("d-none");
            $(`#${this.loadingContainerId}`).addClass("d-none");
        }
    }

    createHtmlTemplate(): void {
        let title = "";
        if (this.isEditable) {
            title = `
                <div class="d-flex align-items-center justify-content-between mb-4">
                    <div id="add-new-container">
                        <button class="btn btn-small revampp-blue-btn" onclick="page.${this.tableVarName}.openAddModal()">+ Add New Animal Model</button>
                    </div>
                    <div class="ml-3">
                        <button id="${this.referenceId}-additional-info-btn" type="button" class="btn btn-circle btn-sm">
                            <i class="fas fa-question"></i>
                        </button>
                    </div>
                </div>
            `
        }

        // Create Content Blocks
        let content = `
            ${title}
            <div id="${this.referenceId}-filter-tab-container" class="d-flex justify-content-between">
                <ul class="nav nav-tabs justify-content-start border-bottom-0" id="${this.referenceId}-filter-tabs" role="tablist">
                    
                </ul>
                <div class="d-flex align-items-center">
                    <div class="font-italic pr-3" id="${this.referenceId}-table-count-found">0 Animal Models Found</div>
                    <div>
                        <button class="btn btn-sm btn-outline-secondary font-size12" onclick="page.${this.tableVarName}.resetFilters()">Reset Filters</button>
                    </div>
                </div>
            </div>

            <div class="tab-content edit-table-filter-tab-container" id="${this.referenceId}-filter-container">
                
            </div>

            <div class="list-table-container mt-4">
                <div class="revampp-datatable border-bottom edit-table-container" id="${this.referenceId}-table-container">
                    
                </div>
            </div>
            <div id="${this.referenceId}-detail-menu-container">

            </div>
        `

        $(`#${this.tableContainerId}`).empty().html(content);

        this.generateFilterTabContent();

    }

    initializeFilterAndTable() {
        // Filter Tabs
        this.generateFilterTabContent();

        // Select Table
        $(`#${this.referenceId}-table-container`).empty().html(
            `<table id="${this.referenceId}-select-table" class="table table-striped selectable-rows w-100" style="width:100%"></table>`
        )

        // Clear Detail Menu Container
        $(`#${this.referenceId}-detail-menu-container`).empty();

        // Create Table
        this.initializeTable();

        // Initialize Modal
        this.initializeAdditionalInfoModal();
    }

    generateFilterTabContent(): void {
        // Create new Tab Content
        let tabContent = "";
        let filtersContent = "";
        for (let i = 0; i < this.filterTabs.length; i++) {
            let currentFilter = this.filterTabs[i];
            let filterNumber = currentFilter.filters.length;
            if (i == 0) {
                tabContent += `<li class="nav-item"><a class="nav-link active" id="${currentFilter.id}-tab" data-toggle="tab" href="#${currentFilter.id}" role="tab" aria-controls="${currentFilter.id}" aria-selected="true">${currentFilter.displayName}</a></li>`
                filtersContent += `<div class="tab-pane fade show active" id="${currentFilter.id}" role="tabpanel" aria-labelledby="${currentFilter.id}-tab"><div class='row'>`
            }
            else {
                tabContent += `<li class="nav-item"><a class="nav-link" id="${currentFilter.id}-tab" data-toggle="tab" href="#${currentFilter.id}" role="tab" aria-controls="${currentFilter.id}" aria-selected="false">${currentFilter.displayName}</a></li>`
                filtersContent += `<div class="tab-pane fade" id="${currentFilter.id}" role="tabpanel" aria-labelledby="${currentFilter.id}-tab"><div class='row'>`
            }

            for (let j = 0; j < currentFilter.filters.length; j++) {
                let filter = currentFilter.filters[j];
                filtersContent += this.generateFilterHtml(filter, filterNumber);
            }

            filtersContent += `</div></div>`
        }

        // Update Tabs
        $(`#${this.referenceId}-filter-tabs`).empty().html(tabContent);
        $(`#${this.referenceId}-filter-container`).empty().html(filtersContent);

        // Generate Dropdown
        for (let i = 0; i < this.filterTabs.length; i++) {
            let currentFilterTab = this.filterTabs[i];
            for (let j = 0; j < currentFilterTab.filters.length; j++) {
                let currentFilter = currentFilterTab.filters[j];
                this.initializeFilterDropdown(currentFilter);
            }
        }

        this.filterTabs.forEach(tab => {
            if (tab.columnViewName) {
                $(`#${tab.id}-tab`).on('click', () => {
                    $(`#column-filter-dropdown-${this.id}-table-column-visibility`).val([`${tab.columnViewName}`])
                    $(`#column-filter-dropdown-${this.id}-table-column-visibility`).trigger("change");
                })
            }
        });
    }

    generateFilterHtml(filter: Filter, numberFilters: number): string {
        let content = "";

        if (filter.type == "am-core-project-filter") {
            content = `
                <div id="${filter.id}-select" class="col-12 col-md-3 mb-2">
                    <div id="${filter.id}-block" class="filter-label d-inline-block label-only">${filter.displayName}</div>
                    <select id="${filter.id}-dropdown" class="selectpicker dropdown" title="None Selected" data-actions-box="true" data-width="100%" data-container="body" data-size="10" multiple>
                    </select>
                </div>
            `
        }
        else {
            content = filter.generateFilterHtml();
        }

        // If there's more than 4 filters in a tab, make them smaller (5 per row)
        if (numberFilters > 4) {
            content = content.replace("col-md-3", "col-md-3 col-lg-2");
        }

        return content;
    }

    initializeFilterDropdown(filter: Filter): void {
        if (filter.type == "am-core-project-filter") {
            // Get Lists
            let centerPickList: PickListItem[] = this.pickLists.find(list => list.Id == "pl-center").Json;
            let allCores: RevamppCore[] = this.pickLists.find(list => list.Id == "pl-all-cores").Json;
            let allProjects: RevamppProject[] = this.pickLists.find(list => list.Id == "pl-all-projects").Json;

            // Find Centers, Cores, Projects (Available)
            let availableCenters: PickListItem[] = [];
            let availableCores: RevamppCore[] = [];
            let corePickList: PickListItemString[] = [];
            let availableProjects: RevamppProject[] = [];
            let projectPickList: PickListItemString[] = [];

            this.animalModels.forEach(model => {
                if (!availableCenters.some(center => center.Id == model.CenterId)){
                    let foundCenter: PickListItem = centerPickList.find(center => center.Id == model.CenterId);
                    if (foundCenter) availableCenters.push(foundCenter);
                }
                

                if (model.CoreIds) {
                    let modelCoreIds: number[] = model.CoreIds.split("|").map(coreIdStr => parseInt(coreIdStr));
                    modelCoreIds.forEach(coreId => {
                        let coreIdVal = `core-${coreId}`;
                        if (!corePickList.some(core => core.Id == coreIdVal)) {
                            let foundCore: RevamppCore = allCores.find(core => core.Id == coreId);
                            if (foundCore) {
                                availableCores.push(foundCore);
                                let coreOption: PickListItemString = {
                                    Id: coreIdVal,
                                    Value: foundCore.CoreName
                                };
                                corePickList.push(coreOption);
                            }
                        }     
                    });
                }
                if (model.ProjectIds) {
                    let modelProjectIds: number[] = model.ProjectIds.split("|").map(projectIdStr => parseInt(projectIdStr));
                    modelProjectIds.forEach(projectId => {
                        let projectIdVal = `project-${projectId}`
                        if (!projectPickList.some(project => project.Id == projectIdVal)) {
                            let foundProject = allProjects.find(project => project.Id == projectId);
                            if (foundProject) {
                                availableProjects.push(foundProject);
                                let projectOption: PickListItemString = {
                                    Id: projectIdVal,
                                    Value: foundProject.ProjectName
                                };
                                projectPickList.push(projectOption);
                            }
                        }
                    });
                }
            });

            // Create Groups
            let dropdownGroups: DropdownGroup[] = [];
            centerPickList.sort((x1, x2) => x1.Value.localeCompare(x2.Value));
            centerPickList.forEach(center => {
                // find cores/projects for each center
                let optionsList: PickListItemString[] = [];
                let foundCores = availableCores.filter(core => core.CenterId == center.Id);
                foundCores.forEach(core => {
                    let coreOption = corePickList.find(option => option.Id == `core-${core.Id}`);
                    if (coreOption) optionsList.push(coreOption);
                })
                let foundProjects = availableProjects.filter(project => project.CenterId == center.Id);
                foundProjects.forEach(project => {
                    let projectOption = projectPickList.find(option => option.Id == `project-${project.Id}`);
                    if (projectOption) optionsList.push(projectOption);
                })
                dropdownGroups.push(new DropdownGroup(center.Value, center.Id.toString(), null, null, optionsList));
            })

            this.initializeGroupFilterDropdown(filter, dropdownGroups, false);
        }
        else {
            if (filter.pickListName) {
                let foundPickList: PickListItem[] = this.pickLists.find(list => list.Id == filter.pickListName).Json;
                if (foundPickList) {
                    filter.initializeDropdown(this.animalModels, this, foundPickList);
                }
            }
            else {
                filter.initializeDropdown(this.animalModels, this);
            }
        }
    }

    initializeGroupFilterDropdown(filter: Filter, dropdownGroups: DropdownGroup[], selectAll: boolean = true): void {
        $(`#${filter.id}-dropdown`).selectpicker({
            dropupAuto: false,
            selectedTextFormat: "count",

            countSelectedText: (numSelected, numTotal) => {
                if (numSelected == numTotal) {
                    return "All";
                }
                else {
                    return numSelected + " Selected";
                }
            }
        });

        let optionContent = "";
        dropdownGroups.forEach(group => {
            optionContent += group.createOptGroupHtml(selectAll);
        });

        $(`#${filter.id}-dropdown`).append(optionContent);

        $(`#${filter.id}-dropdown`).selectpicker("refresh");

        $(`#${filter.id}-dropdown`).on('changed.bs.select', () => {
            this.triggerFilter();
        });
    }

    initializeAdditionalInfoModal(): void {
        $(`#${this.referenceId}-additional-info-btn`).off().on('click', () => {
            $(`#${this.additionalInfoModalId}`).modal('show');
        })
    }

    getSelectedFilterTabId(): string {
        let selectedTab = $(`#${this.referenceId}-filter-tabs`).find(".active").first();
        return selectedTab.prop("id");
    }

    triggerFilter(): void {
        let filteredItems: any[] = _.cloneDeep(this.animalModels);

        this.filterTabs.forEach(tab => {
            tab.filters.forEach(filter => {
                if (filter.type == "am-core-project-filter") {
                    filteredItems = this.filterCoreProject(filteredItems, filter);
                }
                else {
                    filteredItems = filter.filterItems(filteredItems);
                }
            })
        });

        this.updateTable(filteredItems);
    }

    filterCoreProject(items: AnimalModel[], filter: Filter): AnimalModel[] {
        // Get Selected Items
        let selectedItems: AnimalModelOptGroupFilterItem[] = [];
        let selectedOptions = $(`#${filter.id}-dropdown`).find("option:selected");
        let isNoneSelected: boolean = false;
        if (selectedOptions.length == 0) isNoneSelected = true;

        selectedOptions.each(function () {
            let valueStr = <string>$(this).val();
            let parentOptGroup = $(this).closest("optgroup");
            let categoryIdStr = parentOptGroup.data("groupId");
            if (categoryIdStr) {
                let categoryId = parseInt(categoryIdStr);
                if (categoryId && valueStr) {
                    selectedItems.push(new AnimalModelOptGroupFilterItem(categoryId, valueStr));
                }
            }
        });

        // Filter items
        items = items.filter(item => {
            if (isNoneSelected) {
                return true;
            }
            else {
                let centerId = item.CenterId;
                let coreIds = item.CoreIds.split("|").map(coreId => `core-${coreId}`);
                let projectIds = item.ProjectIds.split("|").map(projectId => `project-${projectId}`);

                let hasCenter = selectedItems.some(selectedItem => selectedItem.categoryId == centerId);
                if (!hasCenter) return false;

                let hasCoreProject = selectedItems.some(selectedItem => projectIds.includes(selectedItem.itemId) || coreIds.includes(selectedItem.itemId));
                if (!hasCoreProject) return false;

                return true;
            }
        });

        return items;
    }


    resetFilters(): void {
        this.filterTabs.forEach(tab => {
            tab.filters.forEach(filter => {
                if (filter.type == "am-core-project-filter") {
                    $(`#${filter.id}-dropdown`).selectpicker("deselectAll");
                }
                else {
                    filter.resetFilter();
                }
            })
        });

        this.updateTable(this.animalModels);
    }

    renderPickListItem(data: any, pickListName: string, isMultiple: boolean = false, isSorted: boolean = false, separator: string = ", "): string {
        if (!data) {
            return "";
        }

        let sortedPickList: PickListItemSorted[] = [];
        let pickList: PickListItem[] = [];

        if (isSorted) {
            let foundList: PickListItemSorted[] = this.sortedPickLists.find(list => list.Id == pickListName)?.Json;
            if (foundList) {
                sortedPickList = foundList;
            }
        }
        else {
            let foundList: PickListItem[] = this.pickLists.find(list => list.Id == pickListName)?.Json;
            if (foundList) {
                pickList = foundList;
            }
        }

        if (sortedPickList.length == 0 && pickList.length == 0) {
            return "";
        }

        let returnStrArray: string[] = [];
        let returnStr: string = "";
        if (isMultiple) {
            let stringArrIds: string[] = data.split("|");
            stringArrIds = _.uniq(stringArrIds);

            for (let i = 0; i < stringArrIds.length; i++) {
                let numId: number = parseInt(stringArrIds[i]);
                if (numId) {
                    let foundItem = null;
                    if (isSorted) {
                        foundItem = sortedPickList.find(item => item.Id == numId);
                    }
                    else {
                        foundItem = pickList.find(item => item.Id == numId);
                    }

                    if (foundItem) {
                        returnStrArray.push(foundItem.Value);
                    }
                }
            }

            returnStrArray.sort();
            returnStr = returnStrArray.join(separator);
        }
        else {
            let foundItem = null;
            if (isSorted) {
                foundItem = sortedPickList.find(item => item.Id == data);
            }
            else {
                foundItem = pickList.find(item => item.Id == data);
            }

            if (foundItem) {
                returnStr = foundItem.Value;
            }
        }

        return returnStr;
    }

    renderCoreProject(data: AnimalModel) {
        let coreList: PickListItem[] = this.pickLists.find(list => list.Id == "pl-core").Json;
        let projectList: PickListItem[] = this.pickLists.find(list => list.Id == "pl-project").Json;

        let coreIds = data.CoreIds.split("|");
        let projectIds = data.ProjectIds.split("|");

        let coreNames: string[] = [];
        coreIds.forEach(coreId => {
            let foundCore = coreList.find(core => core.Id.toString() == coreId);
            if (foundCore) coreNames.push(foundCore.Value);
        });
        coreNames.sort();

        let projectNames: string[] = [];
        projectIds.forEach(projectId => {
            let foundProject = projectList.find(project => project.Id.toString() == projectId);
            if (foundProject) projectNames.push(foundProject.Value);
        });
        projectNames.sort();

        let content = "";
        let combinedNames: string[] = [...coreNames, ...projectNames];
        combinedNames.forEach(name => {
            if (content.length != 0) content += "<hr class='my-1'>";
            content += `${name}`;
        });
        return content;
    }


    initializeTable(): void {
        let columns = _.cloneDeep(this.columns);

        let tableColumns: any[] = [];
        let firstColumn = columns.shift();
        if (firstColumn) {
            tableColumns.push(firstColumn);
            if (this.isEditable) {
                tableColumns.push(
                    { data: "Id", title: "", width: "26px", orderable: false, className: "text-center detail-btn-container", render: (data) => this.renderDetailButton(data) }
                )
            }
        }
        tableColumns.push(...columns);

        let fixedColCount = 1;
        if (this.isEditable) fixedColCount = 1;

        this.animalModelTable = $(`#${this.referenceId}-select-table`).DataTable({
            "dom": `<"${this.id}-top-controls"<"${this.id}-column-select"><"${this.id}-search-bar"f><"${this.id}-count-found"B>>rtip`,
            autoWidth: true,
            info: false,
            paging: false,
            search: true,
            scrollX: true,
            //responsive: false,
            scrollY: '60vh',
            scrollCollapse: true,
            orderCellsTop: true,
            fixedColumns: {
                leftColumns: fixedColCount
            },
            language:
            {
                search: "Search",
            },
            columns: tableColumns,
            buttons: [
                {
                    extend: 'csv',
                    filename: "revampp_animal_models",
                    text: '<i class="fas fa-file-download"></i>',
                    titleAttr: 'CSV',
                    charset: 'utf-8'
                }
            ],
            data: this.animalModels,
            order: [[0, 'asc']],
            initComplete: (settings, json) => {

            },
        });

        $(`.${this.id}-top-controls`).addClass('row mx-0 align-items-center');

        $(`.${this.id}-column-select`).addClass('col-12 col-md-3 px-0 mb-2');
        $(`.${this.id}-column-select`).empty().html(`<div id="${this.id}-column-select"></div>`);
        $(`.${this.id}-search-bar`).addClass('col-12 col-md-3');
        $(`.${this.id}-count-found`).addClass('col-12 col-md-6 d-flex justify-content-end vertical-align-middle mt-3 px-0');
        $(`.${this.id}-count-found`).prepend('<div id="selected-items-block" class="align-self-center pr-3"></div>');

        this.tableController = new TableColumnVisibilityControllerV2(`#${this.referenceId}-select-table`, `#${this.id}-column-select`, this.tableColumnGroups, `${this.id}-table-column-visibility`);

        this.updateCount(this.animalModels.length);
        this.visibleAnimalModels = this.animalModels;
    }


    updateCount(numItems: number): void {
        let content = "";
        if (numItems == 1) {
            content = "1 Animal Model Found"
        }
        else {
            content = `${numItems.toString()} Animal Models Found`
        }

        $(`#${this.referenceId}-table-count-found`).text(content);
    }

    renderDetailButton(data: any): string {

        let content = `
            <div class="btn-group dropright">
                <button type="button" class="btn dropdown-toggle p-0" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false" onclick="page.${this.tableVarName}.updateMenuPosition(event)">
                    <div class="item-detail-button" data-id="${data}"><div><i class="fa-solid fa-ellipsis"></i></div></div>
                </button>
                <div class="dropdown-menu revampp-dropdown-menu">
                    
                </div>
            </div>
        `
        return content;
    }

    updateMenuPosition(e: Event) {
        // set current selected row
        let targetElement = <JQuery<HTMLElement>>$(e.currentTarget);
        let table = $(targetElement).closest("table");
        this.setTargetedRow(targetElement);

        // empty any existing menus (menus are moved to this div to avoid overflow issues with table)
        $(`#${this.referenceId}-detail-menu-container`).empty();

        let dropdownMenu = targetElement.siblings('.revampp-dropdown-menu');
        let dropdownContainer = targetElement.parent();

        // Check is the dropdown menu has been moved to the new #detail-menu-container 
        if (dropdownMenu.length <= 0) {
            // if the menu has already been moved, remove existing dropdwon relationship, and create a new one with new menu options
            targetElement.dropdown('dispose');
            dropdownContainer.append(`
                <div class="dropdown-menu revampp-dropdown-menu">
                    ${this.getMenuContent()}
                </div>
            `)
            targetElement.dropdown();
            // move the updated menu to #detail-menu-container when its shown
            dropdownContainer.off().on("show.bs.dropdown", (e) => {
                let updatedDropdownMenu = dropdownContainer.find('.revampp-dropdown-menu');
                $(`#${this.referenceId}-detail-menu-container`).append(<JQuery<HTMLElement>>$(updatedDropdownMenu))
            });
        }
        else {
            // if the menu has not already been moved (first click), just add the correct menu options.
            dropdownMenu.html(`
                ${this.getMenuContent()}
            `)
            // move the updated menu to #detail-menu-container when its shown
            dropdownContainer.off().on("show.bs.dropdown", (e) => {
                let updatedDropdownMenu = dropdownContainer.find(".revampp-dropdown-menu");
                $(`#${this.referenceId}-detail-menu-container`).append(<JQuery<HTMLElement>>$(updatedDropdownMenu));
            });
        }
    }

    setTargetedRow(target: JQuery<HTMLElement>): void {
        let tr = $(target).closest('tr');
        let row = this.animalModelTable.row(tr);
        this.currentTargetItem = <AnimalModel>row.data();
    }

    getMenuContent(): string {
        let menuOptions = "";
        this.editController.sections.forEach(section => {
            let option: string = `<a class="dropdown-item" onclick="page.${this.tableVarName}.openEditModal('${section.id}')"><i class="${section.icon} dropdown-icon pr-2"></i> Edit ${section.title}</a>`
            menuOptions += option;
        });
        return menuOptions;
    }

    resizeTable(): void {
        if (this.animalModelTable) {
            this.animalModelTable.columns.adjust().draw();
        }
    }

    updateTable(items: any[]) {
        // Removes any shown menus
        $("#detail-menu-container").empty();

        this.animalModelTable.clear();
        this.animalModelTable.rows.add(items).draw();
        this.updateCount(items.length);
        this.resizeTable();
        this.visibleAnimalModels = items;
    }

    getCurrentTableSort(): any[] {
        let currentOrder: any[] = this.animalModelTable.order(); // [[0, "asc"], [1, "desc"], ...]
        return currentOrder;
    }

    setCurrentTableSort(sortOrder: any[]): void {
        if (sortOrder.length > 0) {
            for (let i = 0; i < sortOrder.length; i++) {
                let colIndex = sortOrder[i][0];
                let colDir = sortOrder[i][1];
                this.animalModelTable.order([colIndex, colDir]);
            }
            this.animalModelTable.draw();
        }
    }

    scrollToAnimalModel(animalModelId: number = -1): void {
        // Used to indicate new item (will be the largest ID)
        if (animalModelId == -1) {
            let idArr: number[] = this.animalModels.map(am => am.Id);
            let maxVal = Math.max(...idArr);
            animalModelId = maxVal;
        }

        let isExists = false;
        let foundIndex = 0;

        // Find Row
        this.animalModelTable.rows((index: number, data: AnimalModel, node) => {
            if (data?.Id == animalModelId) {
                foundIndex = index;
                isExists = true;
            }
            return false;
        });

        if (!isExists) {
            // If target isn't found do nothing
            return;
        }

        let foundRow = this.animalModelTable.row(foundIndex);
        let foundRowHtml = foundRow.node();

        // Scroll to Row
        let foundRowPosition = $(foundRowHtml).position().top;
        let tableBody = $(`#${this.referenceId}-table-container`).find(".dataTables_scrollBody");
        tableBody.animate({ scrollTop: foundRowPosition }, 400);
    }

    openEditModal(sectionId: string): void {
        this.editController.showLoading(false);
        this.editController.initialize(this.allPickLists, [this.currentTargetItem], sectionId);
        $(`#${this.editController.modalIdTarget}`).modal('show');
    }

    openAddModal(): void {
        let newAnimalModel: AnimalModel = new AnimalModel();

        this.addController.showLoading(true);
        this.addController.initialize(this.allPickLists, newAnimalModel);
        $(`#${this.addController.modalIdTarget}`).modal('show');
    }

    saveEditModal() {
        this.editController.saveEditModal();
    }

    saveAddModal() {
        this.addController.saveAddModal();
    }
}